export type StatCardIcon = "vehicle" | "phone";

export type StatCardItem = {
  id: string;
  value: string | number;
  labelKey: string;
  icon: StatCardIcon;
};

export type TabItem = {
  id: string;
  labelKey: string;
};

export const STATUS_TABS = [
  "VECHILE_STATUS.TABS.IN_YARD",
  "VECHILE_STATUS.TABS.WAITING_ENTRY",
] as const;

export const YARD_STATUS_TABS = [
  "VECHILE_STATUS.TABS.FINISH_RELEASE",
  "VECHILE_STATUS.TABS.READY_TEST",
  "VECHILE_STATUS.TABS.WAITING_TESTS",
  "VECHILE_STATUS.TABS.IN_PRODUCTION",
] as const;

export type VehicleStatusTab = (typeof STATUS_TABS)[number];
export type YardStatusTab = (typeof YARD_STATUS_TABS)[number];

export const VEHICLE_ROW_STATUSES = [
  STATUS_TABS[1],
  ...YARD_STATUS_TABS,
] as const;

export type VehicleRowStatus = (typeof VEHICLE_ROW_STATUSES)[number];

export type DetailField = {
  id: string;
  labelKey: string;
  value: string;
  valueKey?: string;
  variant?: "default" | "success";
};

export type DetailStatusBadge = {
  id: string;
  labelKey: string;
  valueKey?: string;
  variant: "default" | "danger" | "success";
};

export type DetailSection = {
  id: string;
  titleKey: string;
  fields: DetailField[];
  columns: number;
};

export type VehicleDetailSubtitle = {
  makeModelKey: string;
  licenseNumber: string;
  chassisNumber: string;
};

export enum ProductionSubStatus {
  ARRIVED_YARD = "ARRIVED_YARD",
  CHASSIS_PREP = "CHASSIS_PREP",
  READY_PRODUCTION = "READY_PRODUCTION",
  IN_PRODUCTION = "IN_PRODUCTION",
  PROD_TO_WELD = "PROD_TO_WELD",
  IN_WELDING = "IN_WELDING",
  WELD_TO_GRIND = "WELD_TO_GRIND",
  IN_GRINDING = "IN_GRINDING",
  GRIND_TO_PAINT = "GRIND_TO_PAINT",
  IN_PAINT = "IN_PAINT",
  PAINT_TO_FINISH = "PAINT_TO_FINISH",
  IN_FINISHES = "IN_FINISHES",
  DONE_NO_CURTAIN = "DONE_NO_CURTAIN",
  DONE_TEST_READY = "DONE_TEST_READY",
  RELEASE_READY = "RELEASE_READY",
}

export enum TailLiftStatus {
  NOT_ORDERED = "NOT_ORDERED",
  ORDERED = "ORDERED",
  INSTALLED = "INSTALLED",
  NOT_INSTALLED = "NOT_INSTALLED",
  CHECK_AT_TEST = "CHECK_AT_TEST",
  APPROVED = "APPROVED",
}

export enum CurtainStatus {
  TO_MEASURE = "TO_MEASURE",
  MEASURED = "MEASURED",
  ORDERED = "ORDERED",
  RECEIVED = "RECEIVED",
  INSTALLED = "INSTALLED",
}

export enum ProductionItemId {
  CURTAIN = "curtain",
  HYDRAULIC_TAILGATE = "hydraulic-tailgate",
}

export type ProductionStatusData = {
  mainStatusValueKey: string;
  mainStatusHelperKey: string;
  subStatusValue: ProductionSubStatus;
};

export type ProductionItemData = {
  id: ProductionItemId;
  titleKey: string;
  value: string;
};

export type ProductionTabData = {
  status: ProductionStatusData;
  items: ProductionItemData[];
};

export type WeightsFieldData = {
  id: string;
  labelKey: string;
  value: string;
};

export type WeightsTabData = {
  fields: WeightsFieldData[];
  netWeightValueKey: string;
};

export type TestsSignatureStatus = "signed" | "unsigned";

export type TestsSignatureData = {
  id: string;
  labelKey: string;
  status: TestsSignatureStatus;
};

export enum MarkingSheetStatus {
  IN_PROGRESS = "IN_PROGRESS",
  SIGNED_FINISHED = "SIGNED_FINISHED",
}

export type TestsTabData = {
  signatures: TestsSignatureData[];
  markingSheet: {
    value: MarkingSheetStatus;
  };
  inspectionDate: string;
};

export type NotesHistoryItem = {
  id: string;
  roleKey: string;
  timestamp: string;
  contentKey?: string;
  content?: string;
};

export type NotesTabData = {
  draft: string;
  history: NotesHistoryItem[];
};

export type VehicleDetailData = {
  titleKey: string;
  subtitle: VehicleDetailSubtitle;
  badges: DetailStatusBadge[];
  sections: DetailSection[];
  production: ProductionTabData;
  weights: WeightsTabData;
  tests: TestsTabData;
  notes: NotesTabData;
};

export type VehicleTableRow = {
  id: number;
  entryDate: string;
  customerName: string;
  vehicleType: string;
  makeModel: string;
  chassisNo: string;
  panelStatus: string;
  curtainStatus: string;
  inspectionStatus: string;
  plannedFinish: string;
  generalStatus: string;
  workOrderNo: string;
  manufacturer: string;
  model: string;
  hydraulicTailgate: string;
  floor: string;
  approvals: string;
  officeStatus: string;
  productionStatus: string;
  tl: string;
  statusTab: VehicleRowStatus;
};

export type VehicleStatusesState = {
  loading: boolean;
  searchQuery: string;
  yardSearchQuery: string;
  isSearchExtended: boolean;
  isYardSearchExtended: boolean;
  activeStatusTab: VehicleStatusTab;
  activeYardTab: YardStatusTab;
  activeDetailTab: number;
  statCards: StatCardItem[];
  vehicles: VehicleTableRow[];
  selectedVehicleId: number | null;
  selectedVehicleDetail: VehicleDetailData | null;
};
